// Rekognition の Image.Bytes の上限（5MB）。バックエンドはサイズを弾かず SDK にそのまま渡すため、
// 超えるとエラーが不透明なまま判定に失敗する
export const MaxUploadBytes = 5 * 1024 * 1024

const DefaultMaxEdge = 2560

const QualitySteps = [0.85, 0.7, 0.55, 0.4]

const EdgeShrinkRatio = 0.75

const MinEdge = 640

export class ImageDownscaleError extends Error {
  constructor(
    message: string,
    readonly cause?: unknown,
  ) {
    super(message)
    this.name = 'ImageDownscaleError'
  }
}

type Decoded = {
  width: number
  height: number
  draw: (context: CanvasRenderingContext2D, width: number, height: number) => void
  release?: () => void
}

const decode = async (blob: Blob): Promise<Decoded> => {
  // Safari 15 以前は createImageBitmap が無いので Image にフォールバックする
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(blob)
      return {
        width: bitmap.width,
        height: bitmap.height,
        draw: (context, width, height) => context.drawImage(bitmap, 0, 0, width, height),
        // 解放しないと、縮小のたびに元画像ぶんのメモリを抱えたままになる
        release: () => bitmap.close(),
      }
    } catch {
      // Image 読み込みにフォールバック
    }
  }

  const objectUrl = URL.createObjectURL(blob)
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const element = new Image()
      element.onload = () => resolve(element)
      element.onerror = () => reject(new ImageDownscaleError('画像を読み込めませんでした'))
      element.src = objectUrl
    })
    return {
      width: image.naturalWidth,
      height: image.naturalHeight,
      draw: (context, width, height) => context.drawImage(image, 0, 0, width, height),
    }
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}

const toJpegBlob = (canvas: HTMLCanvasElement, quality: number): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => (blob ? resolve(blob) : reject(new ImageDownscaleError('画像を再エンコードできませんでした'))),
      'image/jpeg',
      quality,
    )
  })

const decodeOrThrow = async (blob: Blob): Promise<Decoded> => {
  try {
    return await decode(blob)
  } catch (error) {
    throw error instanceof ImageDownscaleError ? error : new ImageDownscaleError('画像を読み込めませんでした', error)
  }
}

const drawToJpeg = async (decoded: Decoded, maxEdge: number, quality: number): Promise<Blob> => {
  const scale = Math.min(1, maxEdge / Math.max(decoded.width, decoded.height))
  const width = Math.max(1, Math.round(decoded.width * scale))
  const height = Math.max(1, Math.round(decoded.height * scale))

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext('2d')
  if (!context) {
    throw new ImageDownscaleError('canvas を初期化できませんでした')
  }
  decoded.draw(context, width, height)

  return toJpegBlob(canvas, quality)
}

const ReencodeQuality = 0.9

// canvas は透過部分を黒で描画し jpeg にはアルファが無いため、透過は黒くなる（書類の写真なので許容する）
export const reencodeToJpeg = async (blob: Blob): Promise<Blob> => {
  const decoded = await decodeOrThrow(blob)
  try {
    return await drawToJpeg(decoded, Number.POSITIVE_INFINITY, ReencodeQuality)
  } finally {
    decoded.release?.()
  }
}

export const downscaleToLimit = async (blob: Blob, options?: { maxBytes?: number; maxEdge?: number }): Promise<Blob> => {
  const maxBytes = options?.maxBytes ?? MaxUploadBytes
  if (blob.size <= maxBytes) {
    return blob
  }

  const decoded = await decodeOrThrow(blob)

  try {
    let maxEdge = options?.maxEdge ?? DefaultMaxEdge

    while (maxEdge >= MinEdge) {
      for (const quality of QualitySteps) {
        const candidate = await drawToJpeg(decoded, maxEdge, quality)
        if (candidate.size <= maxBytes) {
          return candidate
        }
      }
      maxEdge = Math.floor(maxEdge * EdgeShrinkRatio)
    }
    throw new ImageDownscaleError('画像を上限まで縮小できませんでした')
  } finally {
    decoded.release?.()
  }
}
